import React, { useState, useEffect, useRef } from 'react';
import { projectAPI, customerAPI, userAPI } from '../services/api';
import ProjectModal from './ProjectModal/ProjectModal';
import ProjectBoard from './ProjectBoard/ProjectBoard';
import { PROJECT_TYPES, projectTypeLabel } from '../constants/projectTypes';
import { contactDisplayName } from '../utils/contactName';
import { useAuth } from '../AuthContext';
import './ProjectBoard/projectBoard.css';
import './projectDashboard.css';

const STATUS_NAMES = [
    'Lead', 'Write Estimate', 'Pending Signature', 'Signed Contract',
    'Pending Start Date', 'Jobs In Progress', 'Job Completed',
    'Pending Payments', 'Cancelled', 'Lost Follow Up', 'Paid / Closed'
];

// Backend enum identifiers (PascalCase) for the `statuses` query param.
const STATUS_ENUM_NAMES = [
    'Lead', 'WriteEstimate', 'PendingSignature', 'SignedContract',
    'PendingStartDate', 'JobsInProgress', 'JobCompleted',
    'PendingPayments', 'Cancelled', 'LostFollowUp', 'PaidClosed'
];

const daysInStatus = (statusChangedAt) => {
    if (!statusChangedAt) return null;
    const ms = Date.now() - new Date(statusChangedAt).getTime();
    return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
};

const ProjectDashboard = ({ onViewProject }) => {
    const [projects, setProjects] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingProject, setEditingProject] = useState(null);
    // Prefills the create form (Duplicate). Held in state rather than built inline
    // so its identity is stable — the modal re-seeds whenever the object changes.
    const [seedProject, setSeedProject] = useState(null);
    const [viewMode, setViewMode] = useState('list'); // 'list' | 'board'

    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState([]); // array of enum index numbers
    const [typeFilter, setTypeFilter] = useState([]);     // array of ProjectType index numbers (client-side)
    const [sortBy, setSortBy] = useState('default');      // 'default' | 'daysInStatus'
    const [sortDir, setSortDir] = useState('desc');       // 'asc' | 'desc' (only used with daysInStatus)
    const [filtersOpen, setFiltersOpen] = useState(false);

    // "My jobs" — for people whose work arrives as a job rather than a task. Aaron
    // runs his repairs end to end and does not use the Tasks page at all, so the
    // Projects list is where he needs to find what is his.
    //
    // Assignment is not one field: a rep is the SalesRep, a tech is the
    // ScheduledInstaller, and Aaron is often both on the same repair. Matching only
    // one of them would quietly hide half his work, which is worse than no filter.
    const { user } = useAuth();
    const currentUserId = user?.id ?? null;

    // Remembered per user on this device. A view filter is not layout, and someone
    // else signing in on the same machine must not inherit it.
    const myJobsKey = currentUserId ? `artemis.projects.myJobs.${currentUserId}` : null;
    const [myJobsOnly, setMyJobsOnly] = useState(() => {
        try {
            return myJobsKey ? window.localStorage.getItem(myJobsKey) === '1' : false;
        } catch {
            return false; // private window / storage blocked
        }
    });

    const toggleMyJobs = () => {
        const next = !myJobsOnly;
        setMyJobsOnly(next);
        try {
            if (myJobsKey) window.localStorage.setItem(myJobsKey, next ? '1' : '0');
        } catch {
            /* not worth interrupting the page over */
        }
    };

    const isMine = (p) =>
        currentUserId != null &&
        (p.salesRepId === currentUserId || p.scheduledInstallerId === currentUserId);

    const filtersRef = useRef(null);
    const searchDebounce = useRef(null);

    // Debounced project load when search/filter/sort changes.
    useEffect(() => {
        if (searchDebounce.current) clearTimeout(searchDebounce.current);
        searchDebounce.current = setTimeout(() => {
            loadProjects();
        }, 250);
        return () => clearTimeout(searchDebounce.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, statusFilter, sortBy, sortDir]);

    useEffect(() => {
        loadCustomers();
        loadUsers();
    }, []);

    // Close the filter dropdown on outside click.
    useEffect(() => {
        const handler = (e) => {
            if (filtersRef.current && !filtersRef.current.contains(e.target)) {
                setFiltersOpen(false);
            }
        };
        if (filtersOpen) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [filtersOpen]);

    const loadProjects = async () => {
        try {
            setLoading(true);
            const params = {
                search: searchTerm.trim() || undefined,
                statuses: statusFilter.map(i => STATUS_ENUM_NAMES[i]),
                sortBy,
                sortDir,
            };
            const response = await projectAPI.getAll(params);
            setProjects(response.data);
        } catch (error) {
            setError('Failed to load projects');
            console.error('Error loading projects:', error);
        } finally {
            setLoading(false);
        }
    };

    // Property managers own jobs just as homeowners do — most of the commercial
    // and PM work is booked against a management company, not a person — so both
    // types feed the project modal's contact picker. The picker only lists after
    // you type, so the larger set costs nothing on screen.
    const loadCustomers = async () => {
        try {
            const response = await customerAPI.getAll({
                type: 'Homeowner,PropertyManager',
                limit: 1000,
            });
            setCustomers(response.data);
        } catch (error) {
            console.error('Error loading customers:', error);
            setError('Failed to load customers');
        }
    };

    const loadUsers = async () => {
        try {
            const response = await userAPI.getAll();
            setUsers(response.data);
        } catch (error) {
            console.error('Error loading users:', error);
        }
    };

    const handleAddProject = () => {
        setEditingProject(null);
        setSeedProject(null);
        setShowModal(true);
    };

    const handleEditProject = (project) => {
        setSeedProject(null);
        setEditingProject(project);
        setShowModal(true);
    };

    // Opens the create form pre-filled from an existing job rather than copying it
    // straight away, so the couple of fields that shouldn't carry over — the name,
    // the dates — can be fixed before anything is written.
    const handleDuplicateProject = (project) => {
        setEditingProject(null);
        setSeedProject({ ...project, projectName: `${project.projectName} (Copy)` });
        setShowModal(true);
    };

    const handleDeleteProject = async (projectId) => {
        if (window.confirm('Are you sure you want to delete this project?')) {
            try {
                await projectAPI.delete(projectId);
                loadProjects();
            } catch (error) {
                setError('Failed to delete project');
                console.error('Error deleting project:', error);
            }
        }
    };

    const handleModalClose = () => {
        setShowModal(false);
        setEditingProject(null);
        setSeedProject(null);
        setError('');
    };

    const handleProjectCreated = () => {
        loadProjects();
    };

    const toggleStatus = (i) => {
        setStatusFilter(prev =>
            prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]
        );
    };

    const toggleType = (i) => {
        setTypeFilter(prev =>
            prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]
        );
    };

    const clearFilters = () => {
        setStatusFilter([]);
        setTypeFilter([]);
        setSortBy('default');
        setSortDir('desc');
    };

    const filtersActive = statusFilter.length > 0 || typeFilter.length > 0 || sortBy !== 'default' || myJobsOnly;

    // Project type is filtered client-side — the list already holds every project
    // matching the server-side status/search filters.
    const scopedProjects = myJobsOnly ? projects.filter(isMine) : projects;
    const myJobCount = currentUserId != null ? projects.filter(isMine).length : 0;

    const visibleProjects = typeFilter.length
        ? scopedProjects.filter(p => typeFilter.includes(p.type))
        : scopedProjects;

    const getCustomerName = (project) =>
        project.customer ? contactDisplayName(project.customer) : 'Unknown Customer';

    const getStatusBadgeClass = (status) => {
        const statusMap = {
            0: 'status-pending',     // Lead
            1: 'status-pending',     // WriteEstimate
            2: 'status-pending',     // PendingSignature
            3: 'status-progress',    // SignedContract
            4: 'status-progress',    // PendingStartDate
            5: 'status-progress',    // JobsInProgress
            6: 'status-completed',   // JobCompleted
            7: 'status-pending',     // PendingPayments
            8: 'status-cancelled',   // Cancelled
            9: 'status-cancelled',   // LostFollowUp
            10: 'status-completed'   // PaidClosed
        };
        return statusMap[status] || 'status-pending';
    };

    const getStatusText = (status) => STATUS_NAMES[status] || 'Unknown';

    return (
        <div className="task-dashboard project-dashboard">
            <div className="dashboard-header">
                <div className="header-left">
                    <h2 className="dashboard-title">Projects</h2>
                    <span className="task-count">({visibleProjects.length})</span>
                </div>
                <div className="task-header-right">
                    <div className="view-toggle" role="group" aria-label="View mode">
                        <button
                            className={`view-toggle-btn${viewMode === 'list' ? ' active' : ''}`}
                            onClick={() => setViewMode('list')}
                            title="List view"
                        >
                            <i className="fas fa-list"></i> List
                        </button>
                        <button
                            className={`view-toggle-btn${viewMode === 'board' ? ' active' : ''}`}
                            onClick={() => setViewMode('board')}
                            title="Board view"
                        >
                            <i className="fas fa-columns"></i> Board
                        </button>
                    </div>

                    {viewMode === 'list' && (
                    <div className="search-box">
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Search by name, PO#, customer..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <i className="fas fa-search search-icon"></i>
                    </div>
                    )}

                    {viewMode === 'list' && (
                    <div className="filters-wrapper" ref={filtersRef} style={{ position: 'relative' }}>
                        <button
                            className="btn-create-task"
                            onClick={() => setFiltersOpen(o => !o)}
                            style={{
                                background: filtersActive ? '#0d6efd' : undefined,
                                marginRight: '8px',
                            }}
                        >
                            <i className="fas fa-filter" style={{ marginRight: '6px' }}></i>
                            Filters{filtersActive ? ` (${statusFilter.length + typeFilter.length + (sortBy !== 'default' ? 1 : 0) + (myJobsOnly ? 1 : 0)})` : ''}
                        </button>

                        {/* Outside the Filters panel on purpose: this is the one a
                            field tech uses every time, and burying it behind a menu
                            would defeat the point. */}
                        {currentUserId != null && (
                            <button
                                className={`btn-my-jobs${myJobsOnly ? ' active' : ''}`}
                                onClick={toggleMyJobs}
                                title={myJobsOnly ? 'Show all jobs' : 'Show only jobs assigned to me'}
                            >
                                <i className="fas fa-user-check" style={{ marginRight: '6px' }}></i>
                                My Jobs{myJobCount ? ` (${myJobCount})` : ''}
                            </button>
                        )}

                        {filtersOpen && (
                            <div className="filters-panel">
                                <div style={{ marginBottom: '12px' }}>
                                    <div style={{ fontWeight: 600, marginBottom: '8px' }}>Status</div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px' }}>
                                        {STATUS_NAMES.map((name, i) => (
                                            <label key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={statusFilter.includes(i)}
                                                    onChange={() => toggleStatus(i)}
                                                />
                                                {name}
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div style={{ marginBottom: '12px' }}>
                                    <div style={{ fontWeight: 600, marginBottom: '8px' }}>Project Type</div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px' }}>
                                        {PROJECT_TYPES.map((t) => (
                                            <label key={t.index} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={typeFilter.includes(t.index)}
                                                    onChange={() => toggleType(t.index)}
                                                />
                                                {t.label}
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div style={{ marginBottom: '12px' }}>
                                    <div style={{ fontWeight: 600, marginBottom: '8px' }}>Sort by</div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                            <input
                                                type="radio"
                                                name="sortBy"
                                                checked={sortBy === 'default'}
                                                onChange={() => setSortBy('default')}
                                            />
                                            Status hierarchy (default)
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                            <input
                                                type="radio"
                                                name="sortBy"
                                                checked={sortBy === 'daysInStatus'}
                                                onChange={() => setSortBy('daysInStatus')}
                                            />
                                            Days in status
                                        </label>
                                        {sortBy === 'daysInStatus' && (
                                            <div style={{ marginLeft: '20px', display: 'flex', gap: '12px', marginTop: '4px' }}>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                                    <input
                                                        type="radio"
                                                        name="sortDir"
                                                        checked={sortDir === 'desc'}
                                                        onChange={() => setSortDir('desc')}
                                                    />
                                                    Most days first
                                                </label>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                                    <input
                                                        type="radio"
                                                        name="sortDir"
                                                        checked={sortDir === 'asc'}
                                                        onChange={() => setSortDir('asc')}
                                                    />
                                                    Fewest first
                                                </label>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <button
                                        type="button"
                                        onClick={clearFilters}
                                        disabled={!filtersActive}
                                        style={{
                                            background: 'transparent',
                                            border: '1px solid var(--border-color, #d0d7de)',
                                            borderRadius: '6px',
                                            padding: '4px 12px',
                                            cursor: filtersActive ? 'pointer' : 'not-allowed',
                                            opacity: filtersActive ? 1 : 0.5,
                                            fontSize: '13px',
                                        }}
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                    )}

                    <button className="btn-create-task" onClick={handleAddProject}>
                        Add New Project
                    </button>
                </div>
            </div>

            {error && (
                <div className="error-message">
                    {error}
                </div>
            )}

            {viewMode === 'board' ? (
                <ProjectBoard onViewProject={onViewProject} />
            ) : (
            <div className="projects-container">
                {loading ? (
                    <div className="loading">Loading projects...</div>
                ) : visibleProjects.length === 0 ? (
                    <div className="empty-state">
                        <h3>No projects found</h3>
                        <p>{myJobsOnly
                            ? 'No jobs are assigned to you right now. Turn off My Jobs to see everything.'
                            : (searchTerm || statusFilter.length || typeFilter.length
                                ? 'Try adjusting your search or filters.'
                                : 'Create your first project to get started.')}</p>
                        {!searchTerm && statusFilter.length === 0 && typeFilter.length === 0 && (
                            <button className="btn-create-task" onClick={handleAddProject}>
                                Create Your First Project
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="tasks-table-container">
                        <table className="tasks-table">
                            <thead>
                                <tr>
                                    <th>Project Name</th>
                                    <th>PO #</th>
                                    <th>Customer</th>
                                    <th>Type</th>
                                    <th>Status</th>
                                    <th>Days in Status</th>
                                    <th>Estimated Cost</th>
                                    <th>Created</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleProjects.map((project) => {
                                    const days = daysInStatus(project.statusChangedAt);
                                    return (
                                        <tr key={project.id} className="task-row">
                                            <td className="task-info">
                                                <div
                                                    className="task-title project-link"
                                                    onClick={() => onViewProject(project.id)}
                                                    title="View project details"
                                                >
                                                    {project.projectName}
                                                </div>
                                                {project.description && (
                                                    <div className="task-description">{project.description}</div>
                                                )}
                                            </td>
                                            <td className="po-number" data-label="PO #" style={{ fontFamily: 'monospace', fontSize: '13px', color: 'var(--text-muted, #57606a)' }}>
                                                {project.poNumber || '—'}
                                            </td>
                                            <td className="customer-info" data-label="Customer">
                                                {getCustomerName(project)}
                                            </td>
                                            <td className="project-type-cell" data-label="Type">
                                                <span className="type-pill">{projectTypeLabel(project.type)}</span>
                                            </td>
                                            <td className="project-status" data-label="Status">
                                                <span className={`status-badge ${getStatusBadgeClass(project.status)}`}>
                                                    {getStatusText(project.status)}
                                                </span>
                                            </td>
                                            <td className="days-in-status" data-label="Days in Status">
                                                {days !== null ? `${days}d` : '—'}
                                            </td>
                                            <td className="project-cost" data-label="Est. Cost" style={{ textAlign: 'left', fontWeight: '600', color: '#28a745' }}>
                                                {project.estimatedCost
                                                    ? `$${project.estimatedCost.toLocaleString()}`
                                                    : 'Not estimated'
                                                }
                                            </td>
                                            <td className="due-date" data-label="Created">
                                                {new Date(project.createdAt).toLocaleDateString()}
                                            </td>
                                            <td className="task-actions" data-label="Actions">
                                                <button
                                                    className="action-btn btn-start"
                                                    onClick={() => handleEditProject(project)}
                                                    title="Edit project"
                                                >
                                                    <i className="fas fa-edit"></i>
                                                </button>
                                                <button
                                                    className="action-btn btn-duplicate"
                                                    onClick={() => handleDuplicateProject(project)}
                                                    title="Duplicate project"
                                                    style={{ marginLeft: '8px' }}
                                                >
                                                    <i className="fas fa-copy"></i>
                                                </button>
                                                {/* Admins only, matching the endpoint and the
                                                    contacts table. Deleting a job takes its
                                                    documents, including the S3 files. */}
                                                {user?.role?.toLowerCase() === 'admin' && (
                                                    <button
                                                        className="action-btn btn-complete"
                                                        onClick={() => handleDeleteProject(project.id)}
                                                        title="Delete project"
                                                        style={{ background: '#dc3545', marginLeft: '8px' }}
                                                    >
                                                        <i className="fas fa-trash-alt"></i>
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            )}

            {showModal && (
                <ProjectModal
                    isOpen={showModal}
                    onClose={handleModalClose}
                    onProjectCreated={handleProjectCreated}
                    editingProject={editingProject}
                    seedProject={seedProject}
                    customers={customers}
                    users={users}
                />
            )}
        </div>
    );
};

export default ProjectDashboard;
